import queryString from "query-string";
import type { FilterItem } from "./FilterManagement/CommonFilterTypes";
import {
  isActualStatusKey,
  isSameRemediationItemFilterKey,
} from "./FilterManagement/filterKey";
import type {
  FilterPanelsData,
  FilterTreeNode,
  GetScopeFilterUrlParams,
  ProcessedConditionGroup,
  ProcessFilterResult,
  ProcessScopeResult,
} from "./AppViewUtils.types";

export const pathLevels = {
  MORE_ACTIONS: "moreActions",
  ROOT: "root",
  CURRENT: "current",
  DIRECT_PARENT: "directParent",
} as const;

const isStatusOrNode = (node: FilterTreeNode | undefined): boolean =>
  node?.operator === "or" && isActualStatusKey(node.operands?.[0]?.field);

export const processFilter = (
  filter: FilterTreeNode | undefined,
  res: ProcessFilterResult = { filterConditions: [], index: 0, level: 0 }
): ProcessFilterResult => {
  if (filter?.operator === "or" && !isStatusOrNode(filter)) {
    if (res?.filterConditions) {
      res?.filterConditions.push({ conditions: [] });
    }
    if (res?.level === 0) {
      res?.filterConditions.push({ conditions: [] });
    }
    res.level += 1;

    res = processFilter(filter?.operands?.[0], res);

    res.index++;
    res = processFilter(filter?.operands?.[1], res);
  } else if (filter?.operator) {
    res = processFilter(filter?.operands?.[0], res);
    if (!!filter?.operands?.[1]) {
      res = processFilter(filter?.operands?.[1], res);
    }
  } else {
    if (isActualStatusKey(filter?.field)) {
      let filterVal: { key: unknown; type: unknown }[] = [
        {
          key: filter?.value?.value,
          type: filter?.value?.field,
        },
      ];

      if (filter?.value?.field === "category") {
        const categories: { key: unknown; type: unknown }[] = [];
        if (!!filter?.value?.value && !Array.isArray(filter?.value?.value)) {
          const filterCategoryArray = [filter?.value?.value];
          filter.value.value = filterCategoryArray;
        }
        (filter?.value?.value as unknown[] | undefined)?.forEach((category) => {
          categories.push({
            key: category,
            type: filter?.value?.field,
          });
        });

        filterVal = categories;
      }

      const fieldIndex = res?.filterConditions[
        res.index
      ]?.conditions?.findIndex((condition) =>
        isSameRemediationItemFilterKey(condition.field, filter?.field)
      );

      if (fieldIndex !== undefined && fieldIndex >= 0) {
        res.filterConditions[res.index].conditions[fieldIndex].value = [
          ...(res.filterConditions[res.index].conditions[fieldIndex]
            .value as unknown[]),
          ...filterVal,
        ] as FilterItem["value"];
      } else {
        if (!!res.filterConditions[res.index]?.conditions) {
          res.filterConditions[res.index]?.conditions?.push({
            field: filter?.field ?? null,
            condition: "in",
            value: filterVal as FilterItem["value"],
          });
        } else {
          res.filterConditions[res.index] = {
            conditions: [
              {
                field: filter?.field ?? null,
                condition: "in",
                value: filterVal as FilterItem["value"],
              },
            ],
          };
        }
      }
    } else {
      if (!!res.filterConditions[res.index]?.conditions) {
        res.filterConditions[res.index]?.conditions?.push(
          filter as unknown as FilterItem
        );
      } else {
        res.filterConditions[res.index] = {
          conditions: [filter as unknown as FilterItem],
        };
      }
    }
  }

  return res;
};

export const processScope = (
  filter: FilterTreeNode | undefined,
  res: ProcessScopeResult = { scopeConditions: [], index: 0, level: 0 }
): ProcessScopeResult => {
  if (filter?.operator === "or") {
    if (res?.scopeConditions) {
      res?.scopeConditions.push({ conditions: [] });
    }
    if (res?.level === 0) {
      res?.scopeConditions.push({ conditions: [] });
    }
    res.level += 1;

    res = processScope(filter?.operands?.[0], res);

    res.index++;
    res = processScope(filter?.operands?.[1], res);
  } else if (filter?.operator) {
    res = processScope(filter?.operands?.[0], res);
    res = processScope(filter?.operands?.[1], res);
  } else {
    if (!!res.scopeConditions[res.index]?.conditions) {
      res.scopeConditions[res.index]?.conditions?.push(
        filter as unknown as FilterItem
      );
    } else {
      res.scopeConditions[res.index] = {
        conditions: [filter as unknown as FilterItem],
      };
    }
  }

  return res;
};

export const filterTabs = {
  RECOMMENDED: "recommended",
  SAVED: "saved",
  RECENT: "recent",
} as const;

export const getQueuePluralsByCount = (count: number): string => {
  if (count === 1) {
    return "queue";
  } else if (count > 1) {
    return "queues";
  } else {
    return "";
  }
};

export const getScopeJson = (data: FilterPanelsData | undefined): string => {
  const scopesJson = data?.filterPanels?.reduce<Record<string, unknown>>(
    (panelRes, item, index) => {
      const panelOperands = item?.conditions.reduce<Record<string, unknown>>(
        (res, current, conditionIndex) => {
          if (!current.value) {
            return res;
          }

          const operands = {
            value: current.value,
            field: current.field,
            condition: current.condition,
          };

          let jsonFilter;
          if (conditionIndex === 0) {
            jsonFilter = operands;
          } else {
            jsonFilter = { operator: "and", operands: [res, operands] };
          }

          return jsonFilter;
        },
        {}
      );

      let panelsFilter;
      if (index === 0) {
        panelsFilter = panelOperands;
      } else {
        panelsFilter = {
          operator: "or",
          operands: [panelRes, panelOperands],
        };
      }

      return panelsFilter;
    },
    {}
  );

  return !!scopesJson ? JSON.stringify(scopesJson) : "";
};

const combineFilterOperands = (
  operator: "and" | "or",
  built: FilterTreeNode,
  operand: FilterTreeNode
): FilterTreeNode => {
  if (!Object.keys(operand).length) {
    return built;
  }

  if (!Object.keys(built).length) {
    return operand;
  }

  return { operator, operands: [built, operand] };
};

const isStatusFilterItem = (node: FilterTreeNode): boolean =>
  isActualStatusKey(node.field) || isStatusOrNode(node);

const toFilterPanelOperand = (panel: FilterTreeNode): FilterTreeNode =>
  isStatusFilterItem(panel) ? { operator: "and", operands: [panel] } : panel;

const combineFilterPanels = (
  built: FilterTreeNode,
  panel: FilterTreeNode
): FilterTreeNode => {
  if (!Object.keys(built).length || !Object.keys(panel).length) {
    return combineFilterOperands("or", built, panel);
  }

  return {
    operator: "or",
    operands: [toFilterPanelOperand(built), toFilterPanelOperand(panel)],
  };
};

export const getFilterJson = (data: FilterPanelsData | undefined): string => {
  const filtersJson = data?.filterPanels?.reduce<FilterTreeNode>(
    (panelRes, item) => {
      const panelOperands = item?.conditions.reduce<FilterTreeNode>(
        (res, current) => {
          if (!current.value) {
            return res;
          }

          const operands = {
            value: current.value,
            field: current.field,
            condition: current.condition,
          } as FilterTreeNode;

          if (isActualStatusKey(current.field)) {
            let statusItem: FilterTreeNode;
            const statusValue = current?.value as
              | { type?: string; key: unknown }[]
              | undefined;
            const categories =
              statusValue
                ?.filter((status) => status.type === "category")
                ?.map((status) => status.key) || [];
            const keys =
              statusValue
                ?.filter((status) => status.type === "key")
                ?.reduce<unknown[]>((keysRes, status) => {
                  return [
                    ...keysRes,
                    ...((status.key as unknown[] | undefined) ?? []),
                  ];
                }, []) || [];

            let categoryItem: FilterTreeNode | null = null;
            let keysItem: FilterTreeNode | null = null;

            if (categories?.length) {
              categoryItem = {
                field: current.field,
                condition: "",
                value: {
                  field: "category",
                  condition: "in",
                  value: categories,
                },
              };
            }

            if (keys?.length) {
              keysItem = {
                field: current.field,
                condition: "",
                value: {
                  field: "key",
                  condition: "in",
                  value: keys,
                },
              };
            }

            if (!!categoryItem && !!keysItem) {
              statusItem = {
                operator: "or",
                operands: [categoryItem, keysItem],
              };
            } else if (!!categoryItem) {
              statusItem = categoryItem;
            } else if (!!keysItem) {
              statusItem = keysItem;
            } else {
              return res;
            }

            return combineFilterOperands("and", res, statusItem);
          }

          return combineFilterOperands("and", res, operands);
        },
        {}
      );

      return combineFilterPanels(panelRes, panelOperands);
    },
    {}
  );

  return !!filtersJson ? JSON.stringify(filtersJson) : "";
};

export const getScopeFilterUrl = ({
  selectedScope,
  selectedFilter,
}: GetScopeFilterUrlParams): string => {
  const queryStringObj: Record<string, string | undefined> = {};
  if (!!selectedFilter) {
    queryStringObj.filterId = selectedFilter?.value?.id;
  }
  if (!!selectedScope) {
    queryStringObj.scopeId = selectedScope?.value?.id;
  }

  const queryStringURL = queryString.stringifyUrl({
    url: `${window.location.href}`,
    query: queryStringObj,
  });

  return queryStringURL;
};

export function elementReady(selector: string): Promise<Element> {
  return new Promise((resolve) => {
    const el = document.querySelector(selector);
    if (el) {
      resolve(el);
      return;
    }
    new MutationObserver((mutationRecords, observer) => {
      // Query for elements matching the specified selector
      Array.from(document.querySelectorAll(selector)).forEach((element) => {
        resolve(element);
        //Once we have resolved we don't need the observer anymore.
        observer.disconnect();
      });
    }).observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  });
}

export type { ProcessedConditionGroup };
